import TextField from "@/components/form/hook-form/textfield";
import SegmentedControl from "@/components/toggle/segmented-control";
import { TextButton } from "@/components/ui/buttons";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonBackdrop } from "@ionic/react";
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  convertWeight,
  normalizeInitialWeight,
  normalizeWeightForSubmit,
  sanitizeWeightInput,
} from "../weight-input-modal/weight-utils";

interface WeightInputDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  defaultValue: string;
  defaultUnit: string;
  onSelect: (value: string, unit: "kg" | "lbs") => void;
}

export interface WeightInputDialogHandle {
  focusInput: () => void;
}

type WeightInputFormValues = {
  weight: string;
};

export const WeightInputDialog = memo(
  forwardRef<WeightInputDialogHandle, WeightInputDialogProps>(function WeightInputDialog(
    { isOpen, onDismiss, defaultValue, defaultUnit, onSelect },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    // Proxy input: always in the DOM so iOS can receive focus() synchronously
    // inside the click handler (before the dialog even renders).
    const proxyRef = useRef<HTMLInputElement>(null);
    const [unit, setUnit] = useState<"kg" | "lbs">("kg");
    const form = useForm<WeightInputFormValues>({
      defaultValues: { weight: "" },
    });
    const watchedWeight = useWatch({ control: form.control, name: "weight" });

    useCloseOnBackButton(isOpen, onDismiss);

    useImperativeHandle(ref, () => ({
      focusInput: () => proxyRef.current?.focus(),
    }));

    useEffect(() => {
      if (!isOpen) return;

      setUnit(defaultUnit === "lbs" ? "lbs" : "kg");
      form.reset({ weight: normalizeInitialWeight(defaultValue) });

      // Transfer focus from the proxy to the real input.
      // By this point iOS has already registered keyboard intent from the
      // synchronous focus() in the click handler, so the keyboard stays open.
      const timer = window.setTimeout(() => {
        inputRef.current?.focus();
      }, 50);

      return () => window.clearTimeout(timer);
    }, [defaultUnit, defaultValue, form, isOpen]);

    useEffect(() => {
      const normalized = sanitizeWeightInput(watchedWeight ?? "");
      if (normalized === (watchedWeight ?? "")) return;
      form.setValue("weight", normalized, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }, [form, watchedWeight]);

    const canConfirm = useMemo(() => {
      const parsed = parseFloat(watchedWeight ?? "");
      return Number.isFinite(parsed) && parsed > 0;
    }, [watchedWeight]);

    const handleConfirm = () => {
      const normalized = normalizeWeightForSubmit(watchedWeight ?? "");
      if (!normalized) return;
      onSelect(normalized, unit);
      onDismiss();
    };

    return (
      <>
        {/* Always mounted — lets the parent call focusInput() synchronously */}
        <input
          ref={proxyRef}
          type="text"
          inputMode="decimal"
          aria-hidden="true"
          tabIndex={-1}
          readOnly
          className="pointer-events-none absolute h-0 w-0 opacity-0"
        />

        {isOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center">
            <IonBackdrop
              visible
              tappable
              onIonBackdropTap={onDismiss}
              className="!bg-black !opacity-60"
            />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              className="animate-dialog-enter relative z-10 mx-4 flex w-full max-w-[297px] flex-col gap-4 rounded-4xl bg-white p-5 shadow-xl"
            >
              <h3 className={cn(textVariants.title3, "text-center text-black")}>
                Your Current Weight
              </h3>

              <SegmentedControl
                options={[
                  { value: "kg", label: "kg" },
                  { value: "lbs", label: "lbs" },
                ]}
                value={unit}
                onChange={(val) => {
                  const nextUnit = val as "kg" | "lbs";
                  const converted = convertWeight(watchedWeight ?? "", unit, nextUnit);
                  setUnit(nextUnit);
                  form.setValue("weight", converted, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
                classNames={{
                  container: "h-10 w-full p-1",
                  button: "h-full text-black",
                  buttonActive:
                    "h-full text-black shadow-[-4px_-4px_40px_0px_rgba(215,214,214,0.15),4px_4px_24px_0px_rgba(215,214,214,0.15)]",
                }}
              />

              <TextField
                control={form.control}
                name="weight"
                type="text"
                inputMode="decimal"
                placeholder="Add weight (e.g. 50.3)"
                inputRef={inputRef}
                className="h-[41px] rounded-[24px] border-[var(--color-border-input-default)] px-3 shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]"
              />

              <div className="flex flex-row gap-2">
                <FilledButton
                  color="brand"
                  text="Confirm"
                  disabled={!canConfirm}
                  className="flex-1"
                  onClick={handleConfirm}
                />
                <TextButton onClick={onDismiss} className="flex-1 text-center text-black">
                  Cancel
                </TextButton>
              </div>
            </form>
          </div>
        )}
      </>
    );
  }),
);
